import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Modal, Form, Table, Badge, Row, Col } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaInfoCircle, FaPalette, FaMicrochip, FaTags, FaListAlt, FaTimes, FaBoxOpen } from "react-icons/fa";
import Header from "../header/header";
import "./AdminProduct.css"; 

const API_BASE = process.env.REACT_APP_API_BASE || "https://api-store.crescentworld.com/api/admin/products";
const IMAGE_BASE = "https://api-store.crescentworld.com";

const getAdminAxiosConfig = () => {
  const token = localStorage.getItem("adminToken");
  return { headers: { Authorization: token ? `Bearer ${token}` : "" } };
};

function AdminProduct() {
  const [products, setProducts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // 🔥 NEW: Product Type State
  const [productType, setProductType] = useState("Standard"); // Options: Standard, iPhone, Watch, Mac, iPad, Audio

  const [formData, setFormData] = useState({
    ProductName: "", ProductPrice: "", Category: "", Subcategory: "",
    Description: "", Brand: "", Stock: "", Discount: "", Connectivity: "", Display: "",
    Processor: "", CaseSize: "", CaseMaterial: ""
  });

  const [colors, setColors] = useState([]);
  const [currentColor, setCurrentColor] = useState({ name: "", hex: "#000000" });
  
  const [features, setFeatures] = useState([]);
  const [currentFeature, setCurrentFeature] = useState("");

  const [memory, setMemory] = useState([]);
  const [currentMemory, setCurrentMemory] = useState("");

  const [storage, setStorage] = useState([]);
  const [currentStorage, setCurrentStorage] = useState("");

  const [whatsInTheBox, setWhatsInTheBox] = useState([]);
  const [currentBox, setCurrentBox] = useState("");

  const [addons, setAddons] = useState([]);
  const [currentAddon, setCurrentAddon] = useState({ name: "", price: "" });

  // Universal Variants
  const [variants, setVariants] = useState([]);
  const [variantForm, setVariantForm] = useState({
    color: "", price: "", stock: "", sku: "",
    attributes: [{ key: "", value: "" }]
  });
  
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(API_BASE, getAdminAxiosConfig());
      setProducts(res.data.products || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.clear();
        window.location.href = "/admin";
      }
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  // --- Array Management ---
  const addColor = () => {
    if (currentColor.name.trim()) {
      setColors([...colors, currentColor]);
      setCurrentColor({ name: "", hex: "#000000" });
    }
  };
  const removeColor = (idx) => setColors(colors.filter((_, i) => i !== idx));

  const addFeature = () => {
    if (currentFeature.trim()) {
      setFeatures([...features, currentFeature]);
      setCurrentFeature("");
    }
  };
  const removeFeature = (idx) => setFeatures(features.filter((_, i) => i !== idx));

  const addMemory = () => {
    if (currentMemory.trim()) {
      setMemory([...memory, currentMemory]);
      setCurrentMemory("");
    }
  };
  const removeMemory = (idx) => setMemory(memory.filter((_, i) => i !== idx));

  const addStorage = () => {
    if (currentStorage.trim()) {
      setStorage([...storage, currentStorage]);
      setCurrentStorage("");
    }
  };
  const removeStorage = (idx) => setStorage(storage.filter((_, i) => i !== idx));

  const addBoxItem = () => {
    if (currentBox.trim()) {
      setWhatsInTheBox([...whatsInTheBox, currentBox]);
      setCurrentBox("");
    }
  };
  const removeBoxItem = (idx) => setWhatsInTheBox(whatsInTheBox.filter((_, i) => i !== idx));

  const addAddon = () => {
    if (currentAddon.name && currentAddon.price) {
      setAddons([...addons, { ...currentAddon, price: Number(currentAddon.price) }]);
      setCurrentAddon({ name: "", price: "" });
    }
  };
  const removeAddon = (idx) => setAddons(addons.filter((_, i) => i !== idx));

  // --- Variant Management ---
  const handleAttributeChange = (index, field, value) => {
    const newAttrs = [...variantForm.attributes];
    newAttrs[index][field] = value;
    setVariantForm({ ...variantForm, attributes: newAttrs });
  };
  const addAttributeField = () => setVariantForm({ ...variantForm, attributes: [...variantForm.attributes, { key: "", value: "" }] });
  const removeAttributeField = (index) => setVariantForm({ ...variantForm, attributes: variantForm.attributes.filter((_, i) => i !== index) });

  const addVariant = () => {
    if (!variantForm.price) return alert("Price is required for variant");
    const attributesObj = {};
    variantForm.attributes.forEach(attr => {
      if (attr.key && attr.value) attributesObj[attr.key] = attr.value;
    });

    setVariants([...variants, { ...variantForm, attributes: attributesObj }]);
    setVariantForm({ color: "", price: "", stock: "", sku: "", attributes: [{ key: "", value: "" }] });
  };
  const removeVariant = (index) => setVariants(variants.filter((_, i) => i !== index));

  // --- Modal Actions ---
  const openAddModal = () => {
    setEditMode(false);
    setProductType("Standard");
    setFormData({ ProductName: "", ProductPrice: "", Category: "", Subcategory: "", Description: "", Brand: "", Stock: "", Discount: "", Connectivity: "", Display: "", Processor: "", CaseSize: "", CaseMaterial: "" });
    setColors([]); setFeatures([]); setMemory([]); setStorage([]); setWhatsInTheBox([]); setAddons([]); setVariants([]); setImageFiles([]); setImagePreviews([]);
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditMode(true);
    setSelectedId(product._id);
    
    // 🔥 Auto-detect Product Type to show correct fields
    let type = "Standard";
    if (product.category?.toLowerCase().includes("watch")) type = "Watch";
    else if (product.category?.toLowerCase().includes("mac") || product.category?.toLowerCase().includes("imac")) type = "Mac";
    else if (product.category?.toLowerCase().includes("ipad")) type = "iPad";
    else if (product.category?.toLowerCase().includes("iphone")) type = "iPhone";
    else if (product.category?.toLowerCase().includes("audio")) type = "Audio";
    setProductType(type);

    setFormData({
      ProductName: product.name || "", ProductPrice: product.price || "", Category: product.category || "",
      Subcategory: product.subcategory || "", Description: product.description || "", Brand: product.brand || "",
      Stock: product.stock || "", Discount: product.discount || "", Connectivity: product.connectivity || "",
      Display: product.display || "", Processor: Array.isArray(product.processor) ? product.processor[0] : product.processor || "", 
      CaseSize: product.caseSize || "", CaseMaterial: product.caseMaterial || "",
    });
    
    setColors(product.colors || []); 
    setFeatures(product.features || []); 
    setMemory(product.memory || []); 
    setStorage(product.storage || []); 
    setWhatsInTheBox(product.whatsInTheBox || []);
    setAddons(product.addons || []);
    
    const formattedVariants = (product.variants || []).map(v => {
      const attrs = { ...v };
      delete attrs.color; delete attrs.price; delete attrs.stock; delete attrs.sku; delete attrs._id;
      return { color: v.color || "", price: v.price || "", stock: v.stock || "", sku: v.sku || "", attributes: attrs };
    });
    setVariants(formattedVariants);

    let imgs = [];
    if (Array.isArray(product.images)) imgs = product.images;
    else if (typeof product.images === 'string') imgs = [product.images];
    else if (typeof product.images === 'object') {
       const firstKey = Object.keys(product.images)[0];
       if (firstKey) imgs = product.images[firstKey];
    }
    const imgUrls = Array.isArray(imgs) ? imgs.map(img => img.startsWith("http") ? img : `${IMAGE_BASE}${img}`) : [];
    setImagePreviews(imgUrls);
    setImageFiles([]);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const data = new FormData();

      // Map Form Fields
      data.append("name", formData.ProductName);
      data.append("price", formData.ProductPrice);
      data.append("category", formData.Category);
      data.append("subcategory", formData.Subcategory || "");
      data.append("brand", formData.Brand);
      data.append("description", formData.Description || "");
      data.append("stock", formData.Stock || "0");
      data.append("discount", formData.Discount || "0");
      data.append("connectivity", formData.Connectivity || "");
      data.append("display", formData.Display || "");
      data.append("caseSize", formData.CaseSize || "");
      data.append("caseMaterial", formData.CaseMaterial || "");
      
      if (formData.Processor) data.append("processor", formData.Processor);

      // Append Arrays
      data.append("colors", JSON.stringify(colors));
      data.append("features", JSON.stringify(features));
      data.append("memory", JSON.stringify(memory));
      data.append("storage", JSON.stringify(storage));
      data.append("whatsInTheBox", JSON.stringify(whatsInTheBox));
      data.append("addons", JSON.stringify(addons));
      
      const cleanVariants = variants.map(v => {
        const { attributes, ...rest } = v;
        return { ...rest, ...attributes };
      });
      data.append("variants", JSON.stringify(cleanVariants));

      imageFiles.forEach(file => data.append("images", file));

      const config = getAdminAxiosConfig();
      if (editMode) await axios.put(`${API_BASE}/${selectedId}`, data, config);
      else await axios.post(API_BASE, data, config);

      fetchProducts();
      setShowModal(false);
      alert("Product saved successfully!");
    } catch (error) {
      alert("Error saving product: " + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try { await axios.delete(`${API_BASE}/${id}`, getAdminAxiosConfig()); fetchProducts(); } catch (error) { console.error(error); }
  };

  const getImageUrl = (images) => {
    if (!images) return "https://placehold.co/100x100?text=No+Image";
    let img = Array.isArray(images) ? images[0] : images;
    if (typeof img === "object" && img !== null) {
      const validValues = Object.values(img).filter(val => typeof val === "string");
      img = validValues.length > 0 ? validValues[0] : "";
    }
    if (!img || typeof img !== "string") return "https://placehold.co/100x100?text=No+Image";
    return img.startsWith("http") ? img : `${IMAGE_BASE}${img}`;
  };

  return (
    <div>
      <Header />
      <div className="admin-product-container">
        <div className="container-fluid">
          <div className="admin-header-actions">
            <h2>Product Management</h2>
            <button onClick={openAddModal} className="btn-add-product"><FaPlus /> Add New Product</button>
          </div>

          <div className="admin-table-card">
            <Table responsive className="admin-table">
              <thead>
                <tr>
                  <th>Image</th><th>Product Info</th><th>Price</th><th>Category</th><th>Stock</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p._id}>
                    <td><img src={getImageUrl(p.images)} alt={p.name} className="admin-table-img" onError={(e) => e.target.src = "https://placehold.co/100x100"} /></td>
                    <td><div className="fw-bold">{p.name}</div><small className="text-muted">{p.brand}</small></td>
                    <td className="fw-bold text-success">₹{p.price}</td>
                    <td>{p.category}</td>
                    <td><Badge bg={p.stock > 0 ? "success" : "danger"}>{p.stock || 0} in stock</Badge></td>
                    <td className="text-end">
                      <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEditModal(p)}><FaEdit /></Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(p._id)}><FaTrash /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>

      {/* MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" className="admin-modal" backdrop="static">
        <Modal.Header closeButton><Modal.Title>{editMode ? "Edit Product" : "Create New Product"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSave}>
            
            {/* 🔥 0. Product Type Selector */}
            <div className="p-3 mb-4 bg-light rounded border border-primary">
              <Form.Group>
                <Form.Label className="fw-bold text-primary"><FaMicrochip /> Select Product Type to configure form</Form.Label>
                <Form.Select value={productType} onChange={(e) => setProductType(e.target.value)}>
                  <option value="Standard">Standard / Accessory (No Variants)</option>
                  <option value="iPhone">iPhone (Color + Storage)</option>
                  <option value="iPad">iPad (Color + Storage + Connectivity)</option>
                  <option value="Mac">Mac / iMac (Color + Memory + Storage)</option>
                  <option value="Watch">Apple Watch (Color + Band + Size)</option>
                  <option value="Audio">Audio / Sonos (Color Only)</option>
                </Form.Select>
              </Form.Group>
            </div>

            {/* 1. Basic Info */}
            <div className="form-section">
              <h5 className="form-section-title"><FaInfoCircle /> Basic Info</h5>
              <Row>
                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>Product Images</Form.Label>
                    <Form.Control type="file" multiple onChange={handleFileSelect} />
                    <div className="image-preview-grid">
                      {imagePreviews.map((src, idx) => <img key={idx} src={src} className="image-preview-box" alt="preview" />)}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}><Form.Group><Form.Label>Product Name *</Form.Label><Form.Control name="ProductName" value={formData.ProductName} onChange={handleChange} required /></Form.Group></Col>
                <Col md={6}><Form.Group><Form.Label>Brand *</Form.Label><Form.Control name="Brand" value={formData.Brand} onChange={handleChange} required /></Form.Group></Col>
                <Col md={4}><Form.Group><Form.Label>Base Price (₹) *</Form.Label><Form.Control name="ProductPrice" type="number" value={formData.ProductPrice} onChange={handleChange} required /></Form.Group></Col>
                <Col md={4}><Form.Group><Form.Label>Category *</Form.Label><Form.Control name="Category" value={formData.Category} onChange={handleChange} required /></Form.Group></Col>
                <Col md={4}><Form.Group><Form.Label>Subcategory</Form.Label><Form.Control name="Subcategory" value={formData.Subcategory} onChange={handleChange} /></Form.Group></Col>
              </Row>
            </div>

            {/* 2. Specs & Inventory */}
            <div className="form-section">
              <h5 className="form-section-title"><FaMicrochip /> Specs & Inventory</h5>
              <Row>
                <Col md={3}><Form.Group><Form.Label>Stock *</Form.Label><Form.Control name="Stock" type="number" value={formData.Stock} onChange={handleChange} required /></Form.Group></Col>
                <Col md={3}><Form.Group><Form.Label>Discount (%)</Form.Label><Form.Control name="Discount" type="number" value={formData.Discount} onChange={handleChange} /></Form.Group></Col>
                <Col md={3}><Form.Group><Form.Label>Display</Form.Label><Form.Control name="Display" value={formData.Display} onChange={handleChange} /></Form.Group></Col>
                <Col md={3}><Form.Group><Form.Label>Connectivity</Form.Label><Form.Control name="Connectivity" value={formData.Connectivity} onChange={handleChange} /></Form.Group></Col>
                
                {/* Show Processor for Macs */}
                {(productType === "Mac" || productType === "iPad") && (
                  <Col md={6} className="mt-3"><Form.Group><Form.Label>Processor (Global)</Form.Label><Form.Control name="Processor" value={formData.Processor} onChange={handleChange} placeholder="e.g. Apple M4" /></Form.Group></Col>
                )}
                
                {/* Show Case Size/Material for Watches */}
                {productType === "Watch" && (
                  <>
                    <Col md={3} className="mt-3"><Form.Group><Form.Label>Case Size</Form.Label><Form.Control name="CaseSize" value={formData.CaseSize} onChange={handleChange} placeholder="e.g. 49mm" /></Form.Group></Col>
                    <Col md={3} className="mt-3"><Form.Group><Form.Label>Case Material</Form.Label><Form.Control name="CaseMaterial" value={formData.CaseMaterial} onChange={handleChange} placeholder="e.g. Titanium" /></Form.Group></Col>
                  </>
                )}
                
                <Col md={12} className="mt-3"><Form.Group><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} name="Description" value={formData.Description} onChange={handleChange} /></Form.Group></Col>
              </Row>
            </div>

            <Row>
              <Col md={6}>
                <div className="form-section h-100">
                  <h5 className="form-section-title"><FaPalette /> Colors</h5>
                  <Row className="align-items-end mb-3">
                    <Col sm={5}><Form.Control placeholder="Color Name" value={currentColor.name} onChange={(e) => setCurrentColor({ ...currentColor, name: e.target.value })} /></Col>
                    <Col sm={3}><Form.Control type="color" value={currentColor.hex} onChange={(e) => setCurrentColor({ ...currentColor, hex: e.target.value })} /></Col>
                    <Col sm={4}><Button variant="success-custom" onClick={addColor} className="w-100 text-white">Add</Button></Col>
                  </Row>
                  <div className="d-flex flex-wrap gap-2">
                    {colors.map((color, idx) => (
                      <div key={idx} className="dynamic-item-badge">
                        <span className="color-swatch" style={{ backgroundColor: color.hex }}></span>{color.name}
                        <button type="button" className="btn-remove-item ms-2" onClick={() => removeColor(idx)}>×</button>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-section h-100">
                  <h5 className="form-section-title"><FaTags /> Features</h5>
                  <Row className="align-items-end mb-3">
                    <Col sm={8}><Form.Control placeholder="e.g. Water Resistant" value={currentFeature} onChange={(e) => setCurrentFeature(e.target.value)} /></Col>
                    <Col sm={4}><Button variant="success-custom" onClick={addFeature} className="w-100 text-white">Add</Button></Col>
                  </Row>
                  <div className="d-flex flex-wrap gap-2">
                    {features.map((f, idx) => (
                      <Badge key={idx} bg="dark" className="p-2 dynamic-item-badge text-white">✓ {f}<button type="button" className="btn-remove-item ms-2" onClick={() => removeFeature(idx)}>×</button></Badge>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Show Global Memory & Storage Options ONLY for Macs/iPads */}
            {(productType === "Mac" || productType === "iPad") && (
              <div className="form-section">
                <h5 className="form-section-title"><FaMicrochip /> Global Memory & Storage Options</h5>
                <Row>
                  <Col md={6}>
                    <Row className="align-items-end mb-3">
                      <Col sm={8}><Form.Control placeholder="Add Memory (e.g. 8GB)" value={currentMemory} onChange={(e) => setCurrentMemory(e.target.value)} /></Col>
                      <Col sm={4}><Button variant="success-custom" onClick={addMemory} className="w-100 text-white">Add</Button></Col>
                    </Row>
                    <div className="d-flex flex-wrap gap-2">
                      {memory.map((m, idx) => (<Badge key={idx} bg="info" className="p-2 dynamic-item-badge">{m}<button type="button" className="btn-remove-item ms-2" onClick={() => removeMemory(idx)}>×</button></Badge>))}
                    </div>
                  </Col>
                  <Col md={6}>
                    <Row className="align-items-end mb-3">
                      <Col sm={8}><Form.Control placeholder="Add Storage (e.g. 256GB)" value={currentStorage} onChange={(e) => setCurrentStorage(e.target.value)} /></Col>
                      <Col sm={4}><Button variant="success-custom" onClick={addStorage} className="w-100 text-white">Add</Button></Col>
                    </Row>
                    <div className="d-flex flex-wrap gap-2">
                      {storage.map((s, idx) => (<Badge key={idx} bg="warning" className="p-2 dynamic-item-badge text-dark">{s}<button type="button" className="btn-remove-item ms-2" onClick={() => removeStorage(idx)}>×</button></Badge>))}
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* 4. What's in the box & Add-ons */}
            <div className="form-section">
              <h5 className="form-section-title"><FaBoxOpen /> Box Contents & Add-ons</h5>
              <Row>
                <Col md={6}>
                  <Row className="align-items-end mb-3">
                    <Col sm={8}><Form.Control placeholder="Add Box Item (e.g. USB-C Cable)" value={currentBox} onChange={(e) => setCurrentBox(e.target.value)} /></Col>
                    <Col sm={4}><Button variant="success-custom" onClick={addBoxItem} className="w-100 text-white">Add</Button></Col>
                  </Row>
                  <div className="d-flex flex-wrap gap-2">
                    {whatsInTheBox.map((item, idx) => (<Badge key={idx} bg="secondary" className="p-2 dynamic-item-badge">{item}<button type="button" className="btn-remove-item ms-2" onClick={() => removeBoxItem(idx)}>×</button></Badge>))}
                  </div>
                </Col>
                <Col md={6}>
                  <Row className="align-items-end mb-3">
                    <Col sm={5}><Form.Control placeholder="Addon Name" value={currentAddon.name} onChange={(e) => setCurrentAddon({ ...currentAddon, name: e.target.value })} /></Col>
                    <Col sm={3}><Form.Control type="number" placeholder="Price (₹)" value={currentAddon.price} onChange={(e) => setCurrentAddon({ ...currentAddon, price: e.target.value })} /></Col>
                    <Col sm={4}><Button variant="success-custom" onClick={addAddon} className="w-100 text-white">Add</Button></Col>
                  </Row>
                  <div className="d-flex flex-wrap gap-2">
                    {addons.map((a, idx) => (<Badge key={idx} bg="primary" className="p-2 dynamic-item-badge">{a.name} (₹{a.price})<button type="button" className="btn-remove-item ms-2" onClick={() => removeAddon(idx)}>×</button></Badge>))}
                  </div>
                </Col>
              </Row>
            </div>

            {/* 5. Universal Variants Builder */}
            {productType !== "Standard" && (
              <div className="form-section">
                <h5 className="form-section-title"><FaListAlt /> Product Variants (Configurations)</h5>
                <div className="variant-table-wrapper p-3 mb-3 bg-white border rounded">
                  <Row className="align-items-end mb-2">
                    <Col md={3}><Form.Control placeholder="Color (optional)" value={variantForm.color} onChange={(e) => setVariantForm({ ...variantForm, color: e.target.value })} /></Col>
                    <Col md={3}><Form.Control type="number" placeholder="Price (₹) *" value={variantForm.price} onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })} /></Col>
                    <Col md={3}><Form.Control type="number" placeholder="Stock" value={variantForm.stock} onChange={(e) => setVariantForm({ ...variantForm, stock: e.target.value })} /></Col>
                    <Col md={3}><Form.Control placeholder="SKU" value={variantForm.sku} onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })} /></Col>
                  </Row>

                  <div className="p-3 mb-2 bg-light rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="fw-bold text-muted">
                        Variant Attributes (e.g. {productType === "Watch" ? "Band: Ocean, Size: 49mm" : "Storage: 256GB, Connectivity: Wi-Fi"})
                      </small>
                      <Button size="sm" variant="outline-primary" onClick={addAttributeField}><FaPlus /> Attribute</Button>
                    </div>
                    {variantForm.attributes.map((attr, idx) => (
                      <Row key={idx} className="mb-2">
                        <Col md={5}><Form.Control size="sm" placeholder="Type (e.g. Storage)" value={attr.key} onChange={(e) => handleAttributeChange(idx, "key", e.target.value)} /></Col>
                        <Col md={5}><Form.Control size="sm" placeholder="Value (e.g. 256GB)" value={attr.value} onChange={(e) => handleAttributeChange(idx, "value", e.target.value)} /></Col>
                        <Col md={2}><Button size="sm" variant="outline-danger" className="w-100" onClick={() => removeAttributeField(idx)}><FaTimes /></Button></Col>
                      </Row>
                    ))}
                  </div>

                  <Row className="align-items-end">
                    <Col md={12}><Button variant="success-custom" onClick={addVariant} className="w-100 text-white">Add Variant</Button></Col>
                  </Row>
                </div>

                {variants.length > 0 && (
                  <Table size="sm" className="variant-table bg-white" responsive>
                    <thead>
                      <tr>
                        <th>Color</th><th>Attributes</th><th>Price</th><th>Stock</th><th>SKU</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((variant, idx) => (
                        <tr key={idx}>
                          <td className="fw-bold">{variant.color || "-"}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {Object.entries(variant.attributes).map(([key, val]) => (
                                <Badge key={key} bg="info" className="text-dark">{key}: {val}</Badge>
                              ))}
                            </div>
                          </td>
                          <td className="text-success fw-bold">₹{variant.price}</td>
                          <td>{variant.stock || 0}</td>
                          <td>{variant.sku || "-"}</td>
                          <td><Button size="sm" variant="outline-danger" onClick={() => removeVariant(idx)}>Remove</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            )}

            <Button type="submit" variant="primary" className="w-100 mt-4 btn-primary-submit">{editMode ? "Update Product" : "Publish Product"}</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default AdminProduct;
